function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useMemo } from 'react';
import InputMask from 'react-input-mask';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import { isFunction, isEqual } from 'lodash';
import { fieldProps, getPreferredProp } from "../utils";

/**
 * Input Component
 *
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const InputField = props => {
  const {
    autoComplete = 'on',
    autoFocus = false,
    className = '',
    dataTestId = '',
    disabled = false,
    hideError = false,
    hideHint = false,
    hideLabel = false,
    hint = '',
    inline = false,
    labelClassName = '',
    mask = '',
    max = null,
    min = null,
    maxLength = 99,
    placeholder = '',
    required = false,
    step = 'any',
    style = {
      container: {},
      label: {},
      input: {},
      hint: {},
      error: {}
    },
    type = 'text',
    id,
    label,
    omitLabelStyles = false
  } = props;
  const onChange = event => {
    const callback = getPreferredProp(props, 'onChange');
    if (isFunction(callback)) {
      callback(event);
    }
    if (isFunction(props.onChange) && !isEqual(props.onChange, callback)) {
      props.onChange(event);
    }
  };
  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');
  const name = getPreferredProp(props, 'name');
  const error = getPreferredProp(props, 'error');
  const containerClass = cn({
    'app-components-form-field--has-error': hasError,
    'flex flex-row flex-nowrap': inline,
    'opacity-50': disabled
  }, className);
  const inputClass = cn({
    'h-9 bg-white rounded border border-solid text-text-blue font-sans text-sm tracking-tight px-2 py-3 w-full gap-1': true,
    'mb-1 w-full mt-1': true,
    'border-light-border-blue': !hasError,
    'text-red border-red': hasError,
    'hover:bg-medium-fill-grey focus:border-solid focus:border-focus-blue': !disabled,
    'app-components-form-field--has-error': hasError
  }, className);
  const labelClass = useMemo(() => cn({
    'text-xs mb-0 normal-case tracking-normal font-heavy-font leading-4 flex-none text-right mr-2.5 pt-[14px]': !omitLabelStyles,
    'text-dark-grey': !hasError && !omitLabelStyles,
    'text-red': hasError && !omitLabelStyles,
    'h-9 self-center': inline,
    'sr-only': hideLabel,
    'app-components-form-field__label--required': required
  }, labelClassName, style.label), [required, hasError, labelClassName, hideLabel]);
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass,
    style: style.container,
    children: [/*#__PURE__*/_jsx("label", {
      htmlFor: id,
      className: labelClass,
      style: style.label,
      children: label
    }), /*#__PURE__*/_jsxs("div", {
      children: [mask ? /*#__PURE__*/_jsx(InputMask, {
        className: inputClass,
        autoComplete: autoComplete,
        autoFocus: autoFocus,
        disabled: disabled,
        id: id,
        name: name,
        onBlur: getPreferredProp(props, 'onBlur'),
        onChange: onChange,
        onFocus: getPreferredProp(props, 'onFocus'),
        placeholder: placeholder,
        style: style.input,
        type: type,
        value: getPreferredProp(props, 'value'),
        mask: mask,
        maskChar: "_"
      }) : /*#__PURE__*/_jsx("input", {
        className: inputClass,
        autoComplete: autoComplete,
        autoFocus: autoFocus,
        disabled: disabled,
        id: id,
        max: max,
        maxLength: maxLength,
        min: min,
        name: name,
        onBlur: getPreferredProp(props, 'onBlur'),
        onChange: onChange,
        onFocus: getPreferredProp(props, 'onFocus'),
        placeholder: placeholder,
        step: step,
        style: style.input,
        type: type,
        value: getPreferredProp(props, 'value'),
        "data-testid": dataTestId
      }), !hideHint && hint && /*#__PURE__*/_jsx("div", {
        className: "app-components-form-field__hint",
        style: style.hint,
        children: hint
      }), !hideError && error && /*#__PURE__*/_jsx("div", {
        className: "app-components-form-field__error",
        style: style.error,
        "data-test-visible": hasError,
        "data-testid": "".concat(name || id || 'unnamed', "-error"),
        children: error
      })]
    })]
  });
};
InputField.propTypes = _objectSpread({
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hideLabel: PropTypes.bool,
  hint: PropTypes.node,
  id: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  label: PropTypes.node.isRequired,
  mask: PropTypes.string,
  placeholder: PropTypes.string,
  hideHint: PropTypes.bool,
  hideError: PropTypes.bool,
  required: PropTypes.bool,
  step: PropTypes.string,
  style: PropTypes.shape({
    container: PropTypes.object,
    error: PropTypes.object,
    hint: PropTypes.object,
    input: PropTypes.object,
    label: PropTypes.object
  }),
  type: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  dataTestId: PropTypes.string,
  omitLabelStyles: PropTypes.bool
}, fieldProps);
export default InputField;
//# sourceMappingURL=InputField.js.map